import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { MenuAction } from '@/components/Menu/Actions';
import { I18N_KEYS } from '@/language/keys';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { ThreeDotsVerticalIcon } from '@/UI/Icons/ThreeDotsVerticalIcon';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import type { DeleteFunctions } from './types';

interface MenuBtnProps {
  ariaLabel: string;
  disabled: boolean;
  setDeleteMenuAnchorEl: (anchorEl: HTMLElement | null) => void;
}

const MenuBtnV2 = ({ ariaLabel, disabled, setDeleteMenuAnchorEl }: MenuBtnProps) => (
  <AppIconButton
    aria-label={ariaLabel}
    onClick={(e) => {
      e.stopPropagation();
      setDeleteMenuAnchorEl(e.currentTarget);
    }}
    disabled={disabled}
  >
    <ThreeDotsVerticalIcon width={20} height={20} />
  </AppIconButton>
);

interface DotsMenuProps {
  setDeleteMenuAnchorEl: (anchorEl: HTMLElement | null) => void;
  deleteMenuAnchorEl: HTMLElement | null;
  deletionFunctions: DeleteFunctions;
  disabled: boolean;
  isDeleteAllOthersDisabled?: boolean;
}

const DotsMenu = ({
  setDeleteMenuAnchorEl,
  deleteMenuAnchorEl,
  deletionFunctions,
  disabled,
  isDeleteAllOthersDisabled,
}: DotsMenuProps) => {
  const { t } = useTranslation();
  const { openConfirmationDialog } = useConfirmationDialogStore();

  const items: MenuAction[] = [
    {
      biEventKey: { menu: MENU_NAMES.DOTS_MENU, action: 'delete_current_generation' },
      name: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DELETE_CURRENT_GENERATION),
      action: deletionFunctions?.deleteCurrentResult || noop,
      disabled: deletionFunctions?.disableDelete,
    },
    {
      biEventKey: { menu: MENU_NAMES.DOTS_MENU, action: 'delete_all_others_generations' },
      name: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DELETE_ALL_OTHERS_GENERATIONS),
      action: deletionFunctions?.deleteAllOtherResults || noop,
      disabled: deletionFunctions?.disableDelete || isDeleteAllOthersDisabled,
    },
    {
      type: 'divider',
    },
    {
      biEventKey: { menu: MENU_NAMES.DOTS_MENU, action: 'delete_all_generations' },
      name: t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.DELETE_ALL_GENERATIONS),
      action: () =>
        openConfirmationDialog({
          title: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU_CONFIRMATION_DIALOG.TITLE),
          message: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU_CONFIRMATION_DIALOG.MESSAGE),
          confirmBtnText: t(I18N_KEYS.GENERAL.CLEAR),
          cancelBtnText: t(I18N_KEYS.GENERAL.CANCEL),
          width: 'sm',
          onConfirm: deletionFunctions.deleteAllResults,
        }),
      disabled: deletionFunctions?.disableDelete,
    },
  ];

  return (
    <>
      <MenuBtnV2
        ariaLabel={t(I18N_KEYS.GENERAL.ARIA.OPEN_ITEM_MENU)}
        disabled={disabled}
        setDeleteMenuAnchorEl={setDeleteMenuAnchorEl}
      />
      <AppContextMenu
        width="200px"
        data-testid="design-app-dots-menu"
        open={deleteMenuAnchorEl !== null}
        onClose={(e?: React.MouseEvent) => {
          if (e && 'stopPropagation' in e) {
            e.stopPropagation();
          }
          setDeleteMenuAnchorEl(null);
        }}
        anchorEl={deleteMenuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        items={items}
      />
    </>
  );
};
export default DotsMenu;
